import { useState } from 'react';
import { Pencil, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import type { DetailKunde, KundePatch } from '@/types/detail';
import { FELD_KLASSE } from '@/lib/feldKlassen';

// Die Kundenangaben korrigieren (A2).
//
// Am Tresen wird ein Name aufgenommen, während die Kundin ihn sagt —
// „Musterfrau" statt „Musterfrău", „Meier" statt „Mayer". Bis hierher blieb
// dieser Vertipper für immer stehen: das Formular kannte den Kunden nur
// beim Anlegen.
//
// Geändert wird der STAMMSATZ, nicht eine Kopie am Vorgang. Ein Vertipper
// im Namen ist kein Merkmal dieser einen Bestellung, sondern ein Fehler
// an der Kundin — er gehört an einer Stelle berichtigt (Designprinzip 4).
// Die Lieferadresse bleibt davon unberührt; sie IST eine bewusste Kopie
// am Vorgang und hat ihren eigenen Editor darunter.

type Props = {
  bestellungId: number;
  kunde: DetailKunde;
  onSave: (patch: KundePatch) => void;
};

// `min-h-[44px]` ausdrücklich: `py-2.5` mit `text-sm` ergibt gemessen
// 42 Pixel — zwei zu wenig. Die Polsterung allein trifft die Vorgabe
// nicht, sie kommt ihr nur nahe.
const inputClass = FELD_KLASSE;

function felderVon(k: DetailKunde) {
  return {
    anrede: k.anrede ?? '',
    vorname: k.vorname ?? '',
    nachname: k.nachname ?? '',
    firma: k.firma ?? '',
    email: k.email ?? '',
    telefon: k.telefon ?? '',
    bonus_kundennr: k.bonus_kundennr ?? ''
  };
}

export function KundeEditor({ bestellungId, kunde, onSave }: Props) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(felderVon(kunde));

  // Beim Öffnen den Stand frisch aus den Props ziehen: die Komponente
  // bleibt geschlossen gemountet, und ein zweiter Mitarbeiter kann in der
  // Zwischenzeit etwas geändert haben (B29).
  function oeffnen(): void {
    setF(felderVon(kunde));
    setOpen(true);
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={oeffnen}
        className="inline-flex min-h-[44px] items-center gap-1.5 text-xs text-ink-soft hover:text-akzent"
      >
        <Pencil className="h-3 w-3" />
        Kundenangaben ändern
      </button>
    );
  }

  // Dieselbe Untergrenze wie beim Anlegen und im Server: irgendein Name
  // muss bleiben, sonst ist die Kundin im Dashboard nicht mehr zu finden.
  const nameFehlt =
    f.firma.trim() === '' && f.vorname.trim() === '' && f.nachname.trim() === '';
  const mailKaputt = f.email.trim() !== '' && !f.email.includes('@');

  function speichern(): void {
    if (nameFehlt) return;
    const wert = (v: string): string | null => (v.trim() === '' ? null : v.trim());
    onSave({
      id: bestellungId,
      anrede: wert(f.anrede),
      vorname: wert(f.vorname),
      nachname: wert(f.nachname),
      firma: wert(f.firma),
      email: wert(f.email),
      telefon: wert(f.telefon),
      bonus_kundennr: wert(f.bonus_kundennr)
    });
    setOpen(false);
  }

  return (
    <div className="space-y-2 rounded-md border border-line bg-cream-warm p-3 text-sm">
      {/* Eine Spalte am Telefon, zwei ab 640 Pixeln. Zwei Spalten auf
          390 Pixeln lassen für ein Feld 175 Pixel — darin steht kein
          Nachname, den man noch lesen kann. */}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <label className="text-xs text-ink-soft">
          Anrede
          <input
            className={`${inputClass} mt-1`}
            value={f.anrede}
            maxLength={100}
            onChange={(e) => setF({ ...f, anrede: e.target.value })}
          />
        </label>
        <label className="text-xs text-ink-soft">
          Vorname
          <input
            className={`${inputClass} mt-1`}
            value={f.vorname}
            maxLength={100}
            onChange={(e) => setF({ ...f, vorname: e.target.value })}
          />
        </label>
        <label className="text-xs text-ink-soft">
          Nachname
          <input
            className={`${inputClass} mt-1`}
            value={f.nachname}
            maxLength={100}
            onChange={(e) => setF({ ...f, nachname: e.target.value })}
          />
        </label>
        <label className="text-xs text-ink-soft">
          Firma
          <input
            className={`${inputClass} mt-1`}
            value={f.firma}
            maxLength={255}
            onChange={(e) => setF({ ...f, firma: e.target.value })}
          />
        </label>
        <label className="text-xs text-ink-soft">
          Telefon
          <input
            className={`${inputClass} mt-1`}
            type="tel"
            inputMode="tel"
            value={f.telefon}
            maxLength={50}
            onChange={(e) => setF({ ...f, telefon: e.target.value })}
          />
        </label>
        <label className="text-xs text-ink-soft">
          E-Mail
          <input
            className={`${inputClass} mt-1`}
            type="email"
            inputMode="email"
            value={f.email}
            maxLength={255}
            onChange={(e) => setF({ ...f, email: e.target.value })}
          />
        </label>
        <label className="text-xs text-ink-soft sm:col-span-2">
          BONUS-Kundennummer
          <input
            className={`${inputClass} mt-1`}
            value={f.bonus_kundennr}
            maxLength={32}
            onChange={(e) => setF({ ...f, bonus_kundennr: e.target.value })}
          />
        </label>
      </div>

      {(nameFehlt || mailKaputt) && (
        <ul className="space-y-0.5">
          {nameFehlt && (
            <li className="flex items-start gap-1.5 text-xs text-coral">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              <span>Name oder Firma muss stehen bleiben.</span>
            </li>
          )}
          {mailKaputt && (
            <li className="flex items-start gap-1.5 text-xs text-coral">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              <span>Die E-Mail-Adresse sieht nicht aus wie eine.</span>
            </li>
          )}
        </ul>
      )}

      {/* Was hier geändert wird, gilt für alle Bestellungen dieser
          Kundin. Das ist der Zweck — aber es soll niemanden überraschen. */}
      <p className="text-xs text-ink-faint">
        Gilt für alle Bestellungen dieser Kundin. BONUS bleibt unberührt.
      </p>

      <div className="flex gap-2">
        <Button variant="primary" onClick={speichern} disabled={nameFehlt}>
          Speichern
        </Button>
        <Button variant="ghost" onClick={() => setOpen(false)}>
          Abbrechen
        </Button>
      </div>
    </div>
  );
}
